import React, { useContext, useEffect, useMemo, useState } from "react";
import { GetServerSideProps } from "next";
import { AppContext } from "context/app";
import { CartContext } from "context/cart";
import { UserContext } from "context/user";
import { ModalContext } from "context/modal";
import { LogContext } from "context/log";
import { Cart as PepCart } from "@pepdirect/ui/cart";
import { Spinner } from "@pepdirect/ui/spinner";
import { getCheckoutUrl, cartHasSubscriptionItem } from "helpers/cart";
import {
  generatePageId,
  logCartCheckoutButtonClickEvent,
} from "@pepdirect/helpers/analyticsLogger";
import { cart as cartConfig, endpoints } from "config";
import RockstarHeader from "components/RockstarHeader";
import RockstarFooter from "components/RockstarFooter";
import { WpYoastHead } from "types/wp-yoast-head";
import HeadTagsRenderer from "components/HeadTagsRenderer";
import {
  useFindRecommendationsQuery,
  ItemByGtinQuery,
  ItemByGtinQueryVariables,
  ItemByGtinDocument,
} from "services/graphql/generated";
import TopCtaBanner from "components/TopCtaBanner";
import client from "services/graphql/client";
import { DatoProductFragment, getSdk } from "services/datocms/generated";
import { createDatoClient } from "services/datocms";
import st from "./cart.module.scss";
import { PAGE_TYPE_ENUM } from "@pepdirect/shared/types";

interface CartPageProps {
  products: DatoProductFragment[];
}

export const getServerSideProps: GetServerSideProps<CartPageProps> = async (
  appContext
) => {
  try {
    const { allProducts } = await getSdk(
      createDatoClient(appContext.preview)
    ).products();
    return {
      props: {
        products: allProducts,
      },
    };
  } catch (error) {
    return {
      props: {
        products: [],
      },
    };
  }
};

export default function Cart({ products }: CartPageProps): JSX.Element {
  const cmsProductsSlugs: { gtin: string; slug: string }[] = useMemo(
    () =>
      products?.map((product) => ({
        gtin: product.gtin as string,
        slug: product.slug as string,
      })),
    [products]
  );

  const { tenant } = useContext(AppContext);
  const { cart, loading, getSize, updateCartItem } = useContext(CartContext);
  const { currentUserId } = useContext(UserContext);
  const { open } = useContext(ModalContext);
  const { useLogPageView } = useContext(LogContext);
  const [upsellItems, setUpsellItems] = useState<
    NonNullable<ItemByGtinQuery["itemByGtin"]>[]
  >([]);

  const {
    data: allFindRecommendationsQuery,
    refetch: refetchRecommendedItems,
  } = useFindRecommendationsQuery({
    variables: {
      cartId: cart?.id,
      numberRequested: 150,
    },
  });

  useEffect(() => {
    const getUpsellItems = async () => {
      const recommendedItems = [];
      for (const upsellGtin of allFindRecommendationsQuery?.findRecommendations ||
        []) {
        try {
          const { data } = await client.query<
            ItemByGtinQuery,
            ItemByGtinQueryVariables
          >({
            query: ItemByGtinDocument,
            variables: { gtin: upsellGtin?.gtin as string },
          });
          if (data.itemByGtin) recommendedItems.push(data.itemByGtin);
        } catch (e) {
          // logged in errorLink
        }
      }

      setUpsellItems(recommendedItems.slice(0, 3));
    };

    getUpsellItems();
  }, [allFindRecommendationsQuery]);

  useEffect(() => {
    refetchRecommendedItems();
  }, [cart?.items, refetchRecommendedItems]);

  const checkoutUrl = getCheckoutUrl();
  const size = getSize();

  const { maxCartQty: maxCartQtyDefault } = cartConfig;
  const maxCartQty =
    tenant?.shoppingBagConfig?.maxAmountForSelect || maxCartQtyDefault;

  const minPurchaseAmt = tenant?.shoppingBagConfig?.minimumPurchaseAmount;
  const freeShippingThreshold = tenant?.shippingConfig?.freeShippingThreshold;

  const goToCheckout = () => {
    if (!cart) return;
    logCartCheckoutButtonClickEvent(currentUserId, cart);

    if (currentUserId === null && cartHasSubscriptionItem(cart)) {
      const modal = "CHECKOUT_SUBSCRIPTION_MODAL";
      open(modal);
    } else {
      window.open(checkoutUrl, "_self");
    }
  };

  const seoTags: WpYoastHead = {
    title: "Cart - Rockstar Energy",
    description:
      "Keep that same energy with our offering of Rockstar Energy Products, made with delicious fruit flavored energy to keep you on your grind.",
    canonical: `${endpoints.web}/cart/`,
    og_locale: "en_US",
    og_type: "article",
    og_title: "Cart - Rockstar Energy",
    og_description:
      "Keep that same energy with our offering of Rockstar Energy Products, made with delicious fruit flavored energy to keep you on your grind.",
    og_url: `${endpoints.web}`,
    og_site_name: "Rockstar Energy",
    twitter_card: "summary_large_image",
  };

  useLogPageView(
    {
      pageType: PAGE_TYPE_ENUM.cart,
      pageCategory: undefined,
      pageTitle: seoTags.title || "",
      id: generatePageId("cart"),
    },
    undefined,
    cart
  );

  if (loading) {
    return (
      <div className={st.loading}>
        <Spinner dotSize="25px" />
      </div>
    );
  }

  return (
    <>
      <HeadTagsRenderer yoastJson={seoTags} />

      {/* temp fix until we remove global styles on RSE */}
      <style global jsx>
        {`
          body .site-main {
            font-size: 16px;
          }
        `}
      </style>

      <div id="page" className="site">
        <a className="skip-link screen-reader-text" href="#content">
          Skip to content
        </a>

        <div className="site-header-block">
          <TopCtaBanner />
          <RockstarHeader />
        </div>

        <div id="content" className="site-content">
          <div id="primary" className="content-area">
            <main id="main" className="site-main" role="main">
              {size === 0 || !cart ? (
                <h1
                  className="container has-text-align-center"
                  style={{ margin: "250px 0", color: "#CF9C43" }}
                >
                  YOUR CART IS EMPTY!
                </h1>
              ) : (
                <div className="cart">
                  <PepCart
                    cmsProducts={cmsProductsSlugs}
                    size={size}
                    cart={cart}
                    currentUserId={currentUserId}
                    maxCartQty={maxCartQty}
                    productsImgCdnEndpoint={endpoints.productsImgCdn}
                    minPurchaseAmt={minPurchaseAmt}
                    freeShippingThreshold={freeShippingThreshold}
                    updateCartItem={updateCartItem}
                    goToCheckout={goToCheckout}
                    itemImgCdn={endpoints.productsImgCdn}
                    upsellItems={upsellItems}
                  />
                </div>
              )}
            </main>
          </div>
        </div>

        <RockstarFooter />
      </div>
    </>
  );
}
